import { AnalyticsStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import * as debounce from 'lodash.debounce';
import * as React from 'react';

import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { UitkHeading } from 'uitk-react-text';
import { UitkSlider } from 'uitk-react-slider';
import { UitkSpacing } from 'uitk-react-spacing';

import { FormFieldBinding } from './form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';

export type LocalizedSliderProps = LocalizedComponentProps & SearchFormFieldProps & SliderProps;

export interface SliderProps {
  analytics?: AnalyticsStore;
}

export interface SliderState {
  lowerValue: number;
  upperValue: number;
}

@withLocalization
@FormFieldBinding
@inject('analytics')
export class Slider extends React.Component<LocalizedSliderProps, SliderState> {
  private sendTrackingDebounced;

  constructor(props) {
    super(props);

    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleAfterChange = this.handleAfterChange.bind(this);
    this.formatValueForDisplay = this.formatValueForDisplay.bind(this);
    this.formatSecondaryValueForDisplay = this.formatSecondaryValueForDisplay.bind(this);
    this.sendTrackingDebounced = debounce(this.sendTracking, 300);

    const [lowerValue, upperValue] = this.sliderRange;
    this.state = {
      lowerValue,
      upperValue,
    };
  }

  public componentDidMount() {
    /*
    Used to support legacy 'price' bucket values (i.e. &price=1&price=2) that get translated by the backend to fit with the slider format.
     */
    const { model } = this.props;
    const key = 'priceFilterBuckets';
    if (model && model[key] && model[key].length > 0) {
      const [lowerValue, upperValue] = model[key];
      this.handleStateChange(lowerValue, upperValue);
      this.handleAfterChange(lowerValue, upperValue, undefined, false);
    }
  }

  public componentDidUpdate(prevProps: LocalizedSliderProps): void {
    const { model: prevModel } = prevProps;
    const { model } = this.props;
    if (model !== prevModel) {
      const [lowerValue, upperValue] = this.sliderRange;
      this.handleStateChange(lowerValue, upperValue);
    }
  }

  private get options() {
    const { sortAndFilterOptions, modelKey } = this.props;
    const options = sortAndFilterOptions && sortAndFilterOptions[modelKey];

    return options || {};
  }

  private get sliderRange(): number[] {
    const { model, modelKey, fieldNameOverrides } = this.props;
    const { min: defaultMin, max: defaultMax, step } = this.options;
    const key = (fieldNameOverrides && fieldNameOverrides[modelKey]) || modelKey;
    const selectedRange =
      model?.[key]?.length > 1 && model[key][0] % step === 0 && model[key][1] % step === 0 ? model[key] : [defaultMin, defaultMax];

    return selectedRange.map(parseFloat);
  }

  private handleStateChange(lowerValue: number, upperValue: number): void {
    this.setState({ lowerValue, upperValue });
  }

  private handleAfterChange(
    lowerValue: number,
    upperValue: number,
    _event?: React.ChangeEvent<HTMLInputElement>,
    track: boolean = true
  ): void {
    const { updateModel } = this.props;
    const { name } = this.options;
    const sliderValue = [lowerValue, upperValue];

    if (track) {
      this.sendTrackingDebounced(sliderValue);
    }

    if (typeof updateModel === 'function') {
      updateModel({
        target: {
          name,
          value: sliderValue,
        },
      });
    }
  }

  private sendTracking(value: number[]) {
    const { analytics } = this.props;
    const { name } = this.options;
    const suffix = `${name}Slider.${value}`;
    analytics.clickTracker(`.${suffix}`, suffix, {}, () => null)();
  }

  private formatValueForDisplay(value: number): string {
    const subLabel = this.options.subLabel || '';

    return `${subLabel}${value}`;
  }

  private formatSecondaryValueForDisplay(value: number): string {
    const subLabel = this.options.subLabel || '';
    const displayValue = value === 0 ? `${subLabel}${this.options.max}+` : `${subLabel}${value}`;

    return value === this.options.max ? `${displayValue}+` : displayValue;
  }

  public render() {
    const { l10n } = this.props;
    const { formatText } = l10n;
    const { label, name, max, min, step } = this.options;
    const { lowerValue, upperValue } = this.state;

    return (
      <UitkSpacing margin={{ blockstart: 'six' }}>
        <fieldset data-stid="price-slider-container">
          <UitkSpacing margin={{ blockend: 'four' }}>
            <legend className="filter-title">
              <UitkHeading size={7} tag="h4">
                {label}
              </UitkHeading>
            </legend>
          </UitkSpacing>
          <UitkSlider
            type="dual"
            min={min}
            max={max}
            step={step}
            name={name}
            lowerValue={lowerValue}
            upperValue={Math.max(upperValue, step)}
            id={`${this.props.modelKey}-slider`}
            handleChange={this.handleStateChange}
            handleAfterChange={this.handleAfterChange}
            formatValueForDisplay={this.formatValueForDisplay}
            primaryLabel={formatText('hotels.searchResults.filtersDialog.price.minimumLiveAnnounce')}
            secondaryLabel={formatText('hotels.searchResults.filtersDialog.price.maximumLiveAnnounce')}
            formatSecondaryValueForDisplay={this.formatSecondaryValueForDisplay}
            gap={0}
          />
        </fieldset>
      </UitkSpacing>
    );
  }
}
